import React, { useState, useRef } from 'react'
import { gql } from 'apollo-boost'
import { Alert, Button } from '@smooth-ui/core-sc'
import { useQuery } from '@apollo/react-hooks'
import { Link } from 'react-router-dom'

import trackGMT from 'helpers/gtm'
import { fixTitle } from 'helpers/entities'
import PageWrapper from 'components/PageWrapper/PageWrapper'
import { LoadingSpinnerFullscreen } from 'components/Spinner/Spinner'
import HtmlContent from 'components/HtmlContent/HtmlContent'
import { P } from 'components/Elements'

import AddFavorite from './AddFavorite'
import TraingLog from './TraingLog'
import ReactPlayer, { VideoWrapper } from './VideoWrapper'
import PublicVideoView from './PublicVideoView'

import useTitle from 'helpers/useTitle'
import routeConfig from 'routeConfig'

import { QUERY_ACCESS } from 'helpers/queries'

const NO_ACCESS_MESSAGE = 'Du har ikke tilgang til å se denne treningsvideoen'

const Post = ({
  match: {
    params: { slug },
    url
  },
  location: { state }
}) => {
  const playerRef = useRef(null)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(0)
  const [isDone, setIsDone] = useState(false)
  const { data, loading } = useQuery(query, {
    variables: {
      slug: slug
    }
  })
  const { data: userAccess } = useQuery(QUERY_ACCESS, {
    ssr: false
  })
  const found = data && data.postBy
  const { title, titleView } = useTitle(found ? fixTitle(found.title) : null)
  const hasAccess =
    userAccess && userAccess.me && userAccess.me.membership_level > 0
  const onEnded = () => {
    console.log('onEnded')
    trackGMT({
      event: 'custom',
      category: 'Video',
      action: 'Play complete',
      label: title
    })
    setIsDone(true)
  }
  const onSaved = () => {
    console.log('onSaved')
    trackGMT({
      event: 'custom',
      category: 'Video',
      action: 'TraingLog saved',
      label: title
    })
    setTimeout(() => {
      if (playerRef.current) {
        playerRef.current.seekTo(0, 'seconds')
      }
    }, 500)
  }
  const onReset = () => {
    console.log('onReset', progress)
    trackGMT({
      event: 'custom',
      category: 'Video',
      action: 'Play start',
      label: title
    })
    if (progress) {
      setIsDone(false)
      setProgress(0)
    }
  }
  const accessError = !hasAccess ? NO_ACCESS_MESSAGE : null
  const usedError = error || accessError
  const userNoAccess =
    userAccess && userAccess.me && !userAccess.me.membership_level
  const guestNoAccess = userAccess && !userAccess.me
  return (
    <PageWrapper title={title}>
      {titleView}
      {loading && !found && <LoadingSpinnerFullscreen />}
      {usedError && userNoAccess && (
        <Alert as="div" role="alert">
          <P>
            Din bruker har ikke lenger tilgang til treningsvideoene, vennligst
          </P>
          <Button as={Link} to={routeConfig.mypage}>
            forny ditt abonnement på min side
          </Button>
        </Alert>
      )}
      {usedError && guestNoAccess && (
        <Alert as="div" role="alert">
          <P>Registere deg for full tilgang til nettsiden</P>
          <Button as={Link} to={routeConfig.signup}>
            Register deg nå
          </Button>
        </Alert>
      )}
      <VideoWrapper>
        {found && found.videoId && hasAccess ? (
          <ReactPlayer
            ref={playerRef}
            url={`https://www.vimeo.com/${found.videoId}`}
            width="100%"
            height="100%"
            progressInterval={1000}
            onError={e => setError(e.message)}
            onPlay={onReset}
            onProgress={({ playedSeconds }) => {
              const next = parseInt(playedSeconds)
              if (next !== progress && !isDone) {
                setProgress(next)
              }
            }}
            onEnded={onEnded}
            controls
            data-progress={progress}
            data-ended={isDone}
          />
        ) : (
          <PublicVideoView post={found} freeEnabled={guestNoAccess} />
        )}
      </VideoWrapper>
      {isDone && (
        <TraingLog
          title={title}
          postId={found.postId}
          seconds={progress}
          onSaved={onSaved}
        />
      )}
      {found && <HtmlContent content={found.content} />}
      {hasAccess && found && (
        <AddFavorite postId={found.postId} title={title} />
      )}
    </PageWrapper>
  )
}

const query = gql`
  query Posts($slug: String!) {
    postBy(slug: $slug) {
      id
      postId
      title
      videoId
      content
      featuredImage {
        node {
          id
          altText
          mediaDetails {
            sizes(include: LARGE) {
              sourceUrl
              name
              height
              width
            }
          }
        }
      }
      categories {
        nodes {
          id
          name
        }
      }
    }
  }
`

export default Post
